/**
 * TabCourse — the "Courses" tab body on UniversityDetail.
 *
 * Extracted from UniversityDetail.jsx as part of the P2 refactor. Owns
 * the featured-degrees grid (verified vs editorial card treatments),
 * Oxbridge Tripos explainer, DAAD-complete-coverage badge for German
 * unis, the courses-fallback grid, and the "jump to full directory"
 * empty state.
 *
 * Featured courses are searched, sorted and paged on the server
 * (/universities/{id}/featured-courses, 48 per page); full course
 * detail opens on the course page.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TabWriteup from "@/components/app/university/TabWriteup";
import { courseTuitionUsd } from "@/lib/courseTuition";

function DataRow({ k, v }) {
  return (
    <div className="data-row">
      <div className="data-key">{k}</div>
      <div className="data-val">{v}</div>
    </div>
  );
}

const SORT_OPTIONS = [
  { value: "alphabetical", label: "Alphabetical (A → Z)" },
  { value: "newest",       label: "Newest (2026 first)" },
  { value: "popularity",   label: "Most popular" },
  { value: "cost_asc",     label: "Cost — lowest first" },
  { value: "cost_desc",    label: "Cost — highest first" },
  { value: "acceptance",   label: "Highest acceptance rate" },
  { value: "payback",      label: "Quickest payback (Intl.)" },
];

function CoursesToolbar({ q, setQ, sort, setSort, total, shown, range }) {
  return (
    <div className="mb-5" data-testid="courses-toolbar">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none"
            aria-hidden
          />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search courses at this university (e.g. Classics, Italian, BA)…"
            aria-label="Search courses"
            className="h-12 pl-10 text-base"
            data-testid="courses-search-input"
          />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger
            className="h-12 w-full md:w-[220px] text-sm"
            data-testid="courses-sort-trigger"
          >
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`courses-sort-${o.value}`}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div
        className="mt-2 text-[11px] font-underground-sc tracking-wider text-stone-400"
        data-testid="courses-match-count"
      >
        {q ? `${shown} of ${total} curated` : `${total} curated`}
        {range ? ` · showing ${range}` : ""}
      </div>
    </div>
  );
}

const COURSE_PAGE_SIZE = 48;
const SORT_KEYS = SORT_OPTIONS.map((o) => o.value);

// One server page of this university's featured courses; search, sort and page live in the URL (cq, csort, cpage).
function useFeaturedCourses(uniId) {
  const [params, setParams] = useSearchParams();
  const q = params.get("cq") || "";
  const sort = SORT_KEYS.includes(params.get("csort")) ? params.get("csort") : "alphabetical";
  const page = Math.max(1, parseInt(params.get("cpage"), 10) || 1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const seq = useRef(0);
  const update = (changes, push = false) => {
    const np = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(changes)) { if (v) np.set(k, String(v)); else np.delete(k); }
    setParams(np, { replace: !push });
  };
  useEffect(() => {
    const mine = ++seq.current;
    setError(false);
    const qs = new URLSearchParams({ q, sort, limit: String(COURSE_PAGE_SIZE), offset: String((page - 1) * COURSE_PAGE_SIZE) });
    api.get(`/universities/${encodeURIComponent(uniId)}/featured-courses?${qs}`)
      .then((r) => { if (mine === seq.current) setData(r.data); })
      .catch(() => { if (mine === seq.current) setError(true); });
  }, [uniId, q, sort, page, reload]);
  return { q, sort, page, data, error, update, retry: () => setReload((n) => n + 1) };
}

function CoursesPager({ page, pageCount, onPage }) {
  return (
    <nav className="mt-6 flex items-center justify-between gap-4" aria-label="Course pages" data-testid="courses-pager">
      <Button variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="courses-prev">← Previous</Button>
      <span className="text-sm text-stone-500 font-mono" data-testid="courses-page-indicator">Page {page} of {pageCount}</span>
      <Button variant="outline" disabled={page >= pageCount} onClick={() => onPage(page + 1)} data-testid="courses-next">Next →</Button>
    </nav>
  );
}

export default function TabCourse({ uni, format }) {
  const { q, sort, page, data, error, update, retry } = useFeaturedCourses(uni.id);
  const [input, setInput] = useState(q);
  const topRef = useRef(null);
  useEffect(() => { setInput(q); }, [q]);
  useEffect(() => {
    if (input === q) return undefined;
    const t = setTimeout(() => update({ cq: input.trim() ? input : null, cpage: null }), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);
  const total = data?.total_curated ?? 0;
  const matching = data?.total_matching ?? 0;
  const pageCount = Math.max(1, Math.ceil(matching / COURSE_PAGE_SIZE));
  useEffect(() => {
    if (data && page > pageCount) update({ cpage: pageCount > 1 ? pageCount : null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, page, pageCount]);
  const goToPage = (n) => {
    update({ cpage: n > 1 ? n : null }, true);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const items = data?.items || [];
  const first = matching === 0 ? 0 : (page - 1) * COURSE_PAGE_SIZE + 1;
  const last = Math.min(matching, (page - 1) * COURSE_PAGE_SIZE + items.length);

  if (error && !data) {
    return (
      <>
        <TabWriteup uniId={uni.id} tab="course" />
        <div className="text-sm text-stone-600" data-testid="courses-load-error">
          Couldn't load courses. <button type="button" className="underline" onClick={retry} data-testid="courses-retry">Try again</button>
        </div>
      </>
    );
  }
  return (
    <>
      <TabWriteup uniId={uni.id} tab="course" />
      {!data ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="courses-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-44 rounded-lg border border-stone-200 bg-stone-100/60 animate-pulse" />
          ))}
        </div>
      ) : total > 0 ? (
        <>
          <div ref={topRef} className="flex items-baseline justify-between mb-4 scroll-mt-28">
            <div className="label-uppercase text-stone-600">
              Featured courses
            </div>
            <div
              className="text-[11px] font-underground-sc tracking-wider text-stone-400"
              data-testid="featured-count"
              title="Curated by WUA — a subset of this university's public offering, chosen for coverage of the courses students most often shortlist. Not an exhaustive catalogue."
            >
              {total} curated
            </div>
          </div>

          <CoursesToolbar
            q={input}
            setQ={setInput}
            sort={sort}
            setSort={(v) => update({ csort: v === "alphabetical" ? null : v, cpage: null })}
            total={total}
            shown={matching}
            range={matching > COURSE_PAGE_SIZE ? `${first}–${last}` : null}
          />

          {matching > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="courses-grid">
              {items.map((d) => (
                <FeaturedDegreeCard key={d.id} d={d} format={format} />
              ))}
            </div>
          ) : (
            <div
              className="rounded-md border border-dashed border-stone-300 bg-stone-50 px-4 py-6 text-center text-sm text-stone-600"
              data-testid="courses-no-match"
            >
              No courses at {uni.name} match “{q}”. Try a broader term, or
              search the full directory below.
            </div>
          )}
          {matching > COURSE_PAGE_SIZE && <CoursesPager page={page} pageCount={pageCount} onPage={goToPage} />}

          <div className="mt-6 space-y-3">
            <OxbridgeTriposExplainer uni={uni} />
            <DaadCoverageBadge uni={uni} daadCount={data.daad_count} />
          </div>
        </>
      ) : (uni.courses || []).length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(uni.courses || []).map((c) => (
            <HandCuratedCourseCard key={c.slug} c={c} />
          ))}
        </div>
      ) : (
        <NoFeaturedFallback uniName={uni.name} />
      )}
    </>
  );
}

function OxbridgeTriposExplainer({ uni }) {
  if (uni?.id !== "uk-cambridge" && uni?.id !== "uk-oxford") return null;
  const isCam = uni.id === "uk-cambridge";
  return (
    <div
      className="mb-5 rounded-md border border-[#4C1D95]/30 bg-[#4C1D95]/5 px-4 py-3 flex items-start gap-3"
      data-testid="oxbridge-tripos-explainer"
    >
      <span className="mt-0.5 text-lg leading-none" aria-hidden>📖</span>
      <div className="text-sm text-stone-800 leading-relaxed">
        <b>Why so few courses?</b> {isCam ? "Cambridge" : "Oxford"} uses the{" "}
        <span className="font-mono">
          {isCam ? "Tripos" : "Oxford Schools"}
        </span>{" "}
        system — a small set of broad-entry courses (e.g. Natural Sciences,
        HSPS, PPE) that fan out into dozens of specialist paths in years 2
        and 3. You apply to the broad field, not a niche subject, and
        specialise later. So a catalogue of ~30-40 Triposes here is
        deliberate and correct — each one leads to 100+ distinct final-year
        options. Compare this against a typical UK university that lists
        200+ pre-defined narrow degrees on day one.
      </div>
    </div>
  );
}

function DaadCoverageBadge({ uni, daadCount }) {
  if (uni?.country !== "Germany" || !daadCount) return null;
  return (
    <div
      className="mb-5 rounded-md border border-emerald-300 bg-emerald-50/50 px-4 py-3 flex items-start gap-3"
      data-testid="de-daad-coverage-badge"
    >
      <span className="mt-0.5 text-lg leading-none" aria-hidden>🇩🇪</span>
      <div className="text-sm text-stone-800">
        <b>Complete English-taught coverage</b> — all{" "}
        <span className="font-mono">{daadCount}</span>{" "}
        English-taught bachelor programme{daadCount === 1 ? "" : "s"}{" "}
        that {uni.name} publishes for international students{" "}
        {daadCount === 1 ? "is" : "are"} listed here, sourced directly
        from the{" "}
        <a
          href="https://www2.daad.de/deutschland/studienangebote/international-programmes/"
          target="_blank"
          rel="noreferrer"
          className="text-emerald-800 underline decoration-dotted"
        >
          DAAD International Programmes
        </a>{" "}
        national catalogue.
      </div>
    </div>
  );
}

function verifiedSourceLabel(src) {
  if (!src) return "Gov-data verified";
  if (src.startsWith("hesa"))     return "HESA verified · gov data";
  if (src.startsWith("duo"))      return "DUO verified · gov data";
  if (src.startsWith("cao"))      return "CAO verified · gov data";
  if (src.startsWith("college_scorecard")) return "Scorecard verified · gov data";
  if (src.startsWith("scorecard")) return "Scorecard verified · gov data";
  if (src.startsWith("daad"))     return "DAAD verified · English-taught";
  if (src.startsWith("mur"))      return "MUR verified · gov data";
  return "Gov-data verified";
}

function FeaturedDegreeCard({ d, format }) {
  const alevel = d.entry_requirements?.a_level?.typical;
  const ib = d.entry_requirements?.ib?.typical;
  const offer = d.admissions?.offer_rate;
  const tuit = courseTuitionUsd(d);
  const isVerified = d.source_tier === "verified";
  const stats = d.hesa_stats || {};
  const verifiedLabel = verifiedSourceLabel(d.source_provenance?.source);

  return (
    <Link
      to={`/degree/${d.id}`}
      data-testid={`uni-featured-degree-${d.id}`}
      data-tier={d.source_tier || "editorial"}
      className="group block"
    >
      <Card className={`h-full p-5 border transition-all ${
        isVerified
          ? "border-emerald-200 hover:border-emerald-500 hover:shadow-sm bg-emerald-50/30"
          : "border-stone-200 hover:border-[#4C1D95] hover:shadow-sm"
      }`}>
        <div className="flex items-center justify-between mb-2">
          {isVerified ? (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5"
              data-testid="tier-badge-verified"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-600" />
              {verifiedLabel}
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider text-[#4C1D95] bg-[#F5F0FF] border border-[#E9D5FF] rounded-full px-2 py-0.5"
              data-testid="tier-badge-editorial"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#4C1D95]" />
              Editorially reviewed
            </span>
          )}
          <span className="text-[10px] font-underground-sc tracking-wider text-stone-400 inline-flex items-center gap-1.5">
            {d.first_year_offered === 2026 ? (
              <span
                data-testid={`featured-new-badge-${d.id}`}
                title="New course — first year of offering is 2026"
                className="inline-flex items-center rounded-full bg-[#4C1D95]/10 px-1.5 py-0.5 text-[9px] font-underground-sc tracking-wider text-[#4C1D95]"
              >
                New 2026
              </span>
            ) : null}
            {d.qualification}
          </span>
        </div>
        <div className="label-uppercase text-stone-500">
          {d.subject_area || d.department || "—"}
        </div>
        <h3
          className={`font-editorial text-lg sm:text-xl mt-1 leading-tight text-stone-900 transition-colors ${
            isVerified ? "group-hover:text-emerald-700" : "group-hover:text-[#4C1D95]"
          }`}
        >
          {d.official_title}
        </h3>

        {isVerified ? (
          <VerifiedCardBody d={d} stats={stats} format={format} />
        ) : (
          <EditorialCardBody
            d={d} alevel={alevel} ib={ib} offer={offer} tuit={tuit} format={format}
          />
        )}
      </Card>
    </Link>
  );
}

function VerifiedCardBody({ d, stats, format }) {
  return (
    <>
      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs font-mono text-stone-600">
        <span>{d.duration_years || 3} yrs</span>
        {d.duo_stats?.study_forms?.length > 0 && (
          <span data-testid={`duo-study-forms-${d.id}`}>
            {d.duo_stats.study_forms.map(f => f[0] + f.slice(1).toLowerCase()).join(" · ")}
          </span>
        )}
        {(d.entry_requirements?.a_level?.typical || stats.typical_a_level) && (
          <span data-testid={`verified-alevel-${d.id}`}>
            Typical <span className="text-stone-900">{d.entry_requirements?.a_level?.typical || stats.typical_a_level}</span>
          </span>
        )}
        {stats.continuation_rate != null && (
          <span>{Math.round(stats.continuation_rate * 100)}% continue</span>
        )}
        {stats.nss_avg != null && stats.nss_sample_size >= 30 && (
          <span>NSS {stats.nss_avg}</span>
        )}
      </div>
      {d.duo_stats?.campuses?.length > 0 && (
        <div className="mt-1 text-[11px] text-stone-500" data-testid={`duo-campuses-${d.id}`}>
          {d.duo_stats.campuses.slice(0, 3).join(" · ")}
          {d.duo_stats.campuses.length > 3 && ` · +${d.duo_stats.campuses.length - 3} more`}
        </div>
      )}
      {stats.entry_qualifications?.a_level != null && stats.entry_qualifications.sample_size >= 20 && (
        <div className="mt-1 text-[11px] text-stone-500" data-testid={`verified-entry-${d.id}`}>
          {Math.round(stats.entry_qualifications.a_level)}% entered via A-Level
          {stats.entry_qualifications.baccalaureate >= 3 && (
            <> · {Math.round(stats.entry_qualifications.baccalaureate)}% IB</>
          )}
          {stats.entry_qualifications.access >= 3 && (
            <> · {Math.round(stats.entry_qualifications.access)}% Access</>
          )}
        </div>
      )}
      <div className="mt-3 border-t border-emerald-100 pt-3 flex items-baseline justify-between">
        <VerifiedHeadlineStat d={d} stats={stats} format={format} />
      </div>
      <div className="mt-4 inline-flex items-center gap-1 text-[11px] font-underground-sc tracking-wider text-emerald-700 group-hover:gap-2 transition-all">
        See course + variants →
      </div>
    </>
  );
}

function VerifiedHeadlineStat({ d, stats, format }) {
  if (d.duo_stats) {
    return (
      <>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-700">ECTS</div>
          <div className="font-mono text-lg text-stone-900" data-testid={`verified-ects-${d.id}`}>
            {d.duo_stats.study_load_ects ?? "—"}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-stone-400">Level</div>
          <div className="font-mono text-xs text-stone-500">
            {d.duo_stats.niveau || "—"}
          </div>
        </div>
      </>
    );
  }
  if (d.cao_stats) {
    return (
      <>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-700">CAO Points (2025)</div>
          <div className="font-mono text-lg text-stone-900" data-testid={`verified-cao-pts-${d.id}`}>
            {d.cao_stats.points_r1 ?? d.cao_stats.points_eos ?? "—"}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-stone-400">Level</div>
          <div className="font-mono text-xs text-stone-500">
            {d.cao_stats.level || "—"}
          </div>
        </div>
      </>
    );
  }
  if (d.scorecard_stats) {
    return (
      <>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-700">Median earnings (5yr)</div>
          <div className="font-mono text-lg text-stone-900" data-testid={`verified-earn5-${d.id}`}>
            {d.scorecard_stats.earn_5yr_median_usd
              ? "$" + Math.round(d.scorecard_stats.earn_5yr_median_usd / 1000) + "k"
              : d.scorecard_stats.earn_1yr_median_usd
                ? "$" + Math.round(d.scorecard_stats.earn_1yr_median_usd / 1000) + "k (1yr)"
                : "—"}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-stone-400">Awards / yr</div>
          <div className="font-mono text-xs text-stone-500">
            {d.scorecard_stats.annual_completions ?? "—"}
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-emerald-700">3-YR MEDIAN</div>
        <div className="font-mono text-lg text-stone-900" data-testid={`verified-leo3-${d.id}`}>
          {stats.leo3_median_gbp != null ? format(stats.leo3_median_gbp * 1.27) : "—"}
        </div>
      </div>
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-widest text-stone-400">Sample</div>
        <div className="font-mono text-xs text-stone-500">
          n={stats.leo3_sample_size ?? "—"}
        </div>
      </div>
    </>
  );
}

function EditorialCardBody({ d, alevel, ib, offer, tuit, format }) {
  return (
    <>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs font-mono text-stone-600">
        <span>{d.duration_years || 3} yrs</span>
        {alevel && <span>A-Level: {alevel}</span>}
        {ib && <span>IB: {ib}</span>}
      </div>
      <div className="mt-3 flex items-center justify-between">
        <div className="text-xs text-stone-500">
          {offer != null && (
            <>Offer rate <span className="font-mono text-stone-900">{Math.round(offer * 100)}%</span></>
          )}
        </div>
        <div className="text-xs text-stone-500">
          {tuit != null && (
            <span className="font-mono text-stone-900">{format(tuit)}/yr</span>
          )}
        </div>
      </div>
      <div className="mt-4 inline-flex items-center gap-1 text-[11px] font-underground-sc tracking-wider text-[#4C1D95] group-hover:gap-2 transition-all">
        Open editorial record →
      </div>
    </>
  );
}

function HandCuratedCourseCard({ c }) {
  return (
    <Card className="p-6 border-stone-200">
      <div className="label-uppercase">Subject rank #{c.subject_rank}</div>
      <h3 className="font-editorial text-xl mt-2">{c.name}</h3>
      <div className="mt-4 text-sm">
        <DataRow k="Modules" v={<span className="text-right">{(c.modules || []).slice(0, 4).join(" · ")}</span>} />
        <DataRow k="Specialisms" v={(c.specialisms || []).join(" · ") || "—"} />
        <DataRow k="Teaching" v={c.teaching_style} />
        <DataRow k="Assessment" v={c.assessment} />
        <DataRow k="Class size" v={c.class_size} />
        <DataRow k="Contact h/wk" v={c.contact_hours_week} />
        <DataRow k="Accreditation" v={(c.accreditations || []).join(", ") || "—"} />
        <DataRow k="Placement year" v={c.placement_year ? "Yes" : "No"} />
        <DataRow k="Study abroad" v={c.study_abroad ? "Yes" : "No"} />
        <DataRow k="Flexibility" v={c.flexibility} />
      </div>
    </Card>
  );
}

function NoFeaturedFallback({ uniName }) {
  return (
    <Card
      className="p-6 border-stone-200 bg-stone-50 text-center"
      data-testid="course-tab-scroll-cta"
    >
      <div className="label-uppercase !text-[10px] text-stone-500">
        Flagship editorial profiles coming soon
      </div>
      <p className="mt-3 text-sm text-stone-600 max-w-xl mx-auto leading-snug">
        Hand-picked flagship course profiles are being written for{" "}
        {uniName}. In the meantime, every degree we have data on is listed
        just below — click any course to open its full record.
      </p>
      <button
        type="button"
        onClick={() => {
          document
            .getElementById("directory-courses")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#4C1D95] hover:text-[#3B0764] underline decoration-dotted"
      >
        Jump to the full course directory ↓
      </button>
    </Card>
  );
}
